import { findWhere, pick, extend, isEmpty } from 'underscore';
import { ScopeLevel } from '@bingads-webui-campaign-react/constants';

import { bulkModelToViewModelForConversionGoals, modelToViewModelForConversionGoals } from './campaign-conversion';
import {
  bulkModelToViewModelForLocationTargets,
  modelToViewModelForLocationTargets,
  modelToViewModelForDayTimeTargets,
  bulkModelToViewModelForDayTimeTargets,
  modelToViewModelForDeviceTargets,
  modelToViewModelForGenderTargets,
  modelToViewModelForAgeTargets,
  modelToViewModelForCompanyNameTargets,
  modelToViewModelForIndustryTargets,
  modelToViewModelForJobFunctionTargets,
} from './campaign-criterion';
import { bulkModelToViewModelForDisclaimer, modelToViewModelForDisclaimer } from './ad-extension';
import { modelToViewModelForMultiMediaBidAdjustment, modelToViewModelForAutomatedCallToActionOptOut, viewModelToModelForMultiMediaAdBidAdjustment } from './adjustment';
import { modelToViewModelForDsaSettings, bulkModelToViewModelForDsaSettings } from './dsa-settings';
import { viewModelToModelForPredictiveMatching } from './predictive-matching';

const whitelistedSchemaPropertyKeysForModel = [
  'Id', 'Name', 'Status', 'BiddingScheme', 'Budget', 'Languages', 'AdGroups', 'AutoGeneratedAssetsEnabled', 'AdScheduleUseSearcherTimeZone', 'DisclaimerAdsEnabled', 'AdExtensionAssociations', 'DomainName', 'LanguageCode', 'Source', 'DynamicDescriptionEnabled', 'TrackingUrlTemplate', 'FinalUrlSuffix', 'UrlCustomParameters', 'PageFeedIds', 'AutoTargetingEnabled',
];

const editSpecialProperty = [
  'MultiMediaAdBidAdjustment', 'AutomatedCallToActionOptOut', 'CampaignSettings', 'BidStrategyName', 'ExperimentId',
];

const whitelistedSchemaPropertyKeysForModelEdit = whitelistedSchemaPropertyKeysForModel.concat(editSpecialProperty);

const whitelistedSchemaPropertyKeys = whitelistedSchemaPropertyKeysForModel.concat(['CampaignConversionGoal']);
const whitelistedSchemaPropertyKeysEdit = whitelistedSchemaPropertyKeys.concat([...editSpecialProperty, 'ImportProperties']);

const viewModelToModelForUrlCustomParameters = urlCustomParametersViewModel =>
  urlCustomParametersViewModel.filter(({ Key }) => Key);

export const viewModelToModelForCampaign = (campaignViewModel, isEdit, i18n, permissions) => {
  let model = {
    ...pick(campaignViewModel, isEdit ? whitelistedSchemaPropertyKeysForModelEdit : whitelistedSchemaPropertyKeysForModel),
  };

  if (isEdit) {
    if (!permissions.IsDomesticSimplifiedChineseEnabled || (permissions.IsDomesticSimplifiedChineseEnabled && permissions.dynamic.MMAForCN)) {
      model = extend(model, viewModelToModelForMultiMediaAdBidAdjustment(ScopeLevel.Campaign, campaignViewModel, i18n));
    } else {
      delete model.MultimediaAdBidAdjustment;
      delete model.MultiMediaAdBidAdjustment;
    }

    if (campaignViewModel.BiddingScheme) {
      model = extend(model, viewModelToModelForPredictiveMatching(campaignViewModel, permissions));
    }

    if (campaignViewModel.Budget) {
      model.BudgetId = campaignViewModel.Budget.Id;
    }

    if (campaignViewModel.UrlCustomParameters) {
      model.UrlCustomParameters = viewModelToModelForUrlCustomParameters(campaignViewModel.UrlCustomParameters);
    }
  }
  return model;
};

export const bulkModelToViewModelForCampaign = ({
  campaignModel,
  originalCampaignModel,
  bundleCountries,
  errors,
  isEdit,
  permissions,
}) => {
  let data = {
    ...pick(campaignModel, whitelistedSchemaPropertyKeysForModel),
  };
  data = extend(data, bulkModelToViewModelForLocationTargets({
    locationTargetsModel: campaignModel.CampaignCriterions,
    bundleCountries,
  }));
  data = extend(data, bulkModelToViewModelForConversionGoals({
    campaignModel,
    originalCampaignModel,
    errors,
  }));
  data = extend(data, bulkModelToViewModelForDisclaimer({
    campaignModel,
    originalCampaignModel,
  }));

  if (isEdit) {
    data.multimediaAds = originalCampaignModel.multimediaAds;
    data.AutomatedCallToActionOptOut = originalCampaignModel.multimediaAds;
    data.Device = originalCampaignModel.Device;
    data.initialDevice = originalCampaignModel.initialDevice;
    data.Gender = originalCampaignModel.Gender;
    data.initialGender = originalCampaignModel.initialGender;
    data.Age = originalCampaignModel.Age;
    data.initialAge = originalCampaignModel.initialAge;
    data.companyNameTargets = originalCampaignModel.companyNameTargets;
    data.initialCompanyNameTargets = originalCampaignModel.initialCompanyNameTargets;
    data.industry = originalCampaignModel.industry;
    data.initialIndustry = originalCampaignModel.initialIndustry;
    data.initialIndustryTargets = originalCampaignModel.initialIndustryTargets;
    data.jobTargets = originalCampaignModel.jobTargets;
    data.initialJobTargets = originalCampaignModel.initialJobTargets;
    data.initialJobFunctionTargets = originalCampaignModel.initialJobFunctionTargets;

    if (campaignModel.CampaignCriterions) {
      data.dayTimeRanges = bulkModelToViewModelForDayTimeTargets(campaignModel.CampaignCriterions, originalCampaignModel.dayTimeRanges || []);
      data.adSchedulesBulkUpsert = [];
    }

    if (campaignModel.BidStrategyName) {
      data.Portofolio = campaignModel.BiddingScheme;
    }

    data = extend(data, bulkModelToViewModelForDsaSettings({
      campaignModel,
      permissions,
    }));
  }
  return data;
};

export const modelToViewModelForCampaign = ({
  campaignModel,
  locationCriterionsModel,
  bundleCountries,
  disclaimerAssociations,
  scheduleCriterionModel,
  targetCriterionModel,
  pageFeeds,
  isEdit,
  permissions,
}) => {
  let viewModel = {
    ...pick(campaignModel, isEdit ? whitelistedSchemaPropertyKeysEdit : whitelistedSchemaPropertyKeys),
  };

  if (campaignModel.Status === 'UserPaused') {
    viewModel.Status = 'Paused';
  }

  if (locationCriterionsModel) {
    viewModel = extend(viewModel, modelToViewModelForLocationTargets({
      model: locationCriterionsModel,
      bundleCountries,
      campaignId: campaignModel.Id,
    }));
  }

  viewModel = extend(viewModel, modelToViewModelForDisclaimer({
    disclaimerAssociations,
  }));

  if (scheduleCriterionModel && !isEmpty(scheduleCriterionModel)) {
    viewModel.dayTimeRanges = modelToViewModelForDayTimeTargets(scheduleCriterionModel);
  }

  if (campaignModel.CampaignSettings) {
    const disclaimerSetting = findWhere(campaignModel.CampaignSettings, { '@odata.type': '#Model.DisclaimerSetting' });
    viewModel.DisclaimerAdsEnabled = (disclaimerSetting && disclaimerSetting.DisclaimerAdsEnabled) || false;

    const rsaSetting = findWhere(campaignModel.CampaignSettings, { '@odata.type': '#Model.ResponsiveSearchAdsSetting' });
    viewModel.AutoGeneratedAssetsEnabled = (rsaSetting && rsaSetting.AutoGeneratedAssetsEnabled) || false;

    const predictiveTargetingSetting = findWhere(campaignModel.CampaignSettings, { '@odata.type': '#Model.PredictiveTargetingSetting' });
    viewModel.AutoTargetingEnabled = (predictiveTargetingSetting && predictiveTargetingSetting.PredictiveTargetingEnabled) || false;
  }

  viewModel = extend(viewModel, modelToViewModelForConversionGoals(campaignModel));
  if (isEdit) {
    viewModel = extend(viewModel, modelToViewModelForMultiMediaBidAdjustment(ScopeLevel.Campaign, campaignModel.MultiMediaAdBidAdjustment));
    viewModel = extend(viewModel, modelToViewModelForAutomatedCallToActionOptOut(campaignModel));

    viewModel = extend(viewModel, modelToViewModelForDeviceTargets({
      model: targetCriterionModel.filter(target => target.Criterion['@odata.type'] === '#Model.DeviceCriterion'),
      campaignId: campaignModel.Id,
    }));

    viewModel = extend(viewModel, modelToViewModelForDsaSettings({
      campaignModel,
      permissions,
    }));

    viewModel = extend(viewModel, { pageFeeds });

    viewModel = extend(viewModel, modelToViewModelForGenderTargets({
      model: targetCriterionModel.filter(target => target.Criterion['@odata.type'] === '#Model.GenderCriterion'),
      campaignId: campaignModel.Id,
    }));

    viewModel = extend(viewModel, modelToViewModelForAgeTargets({
      model: targetCriterionModel.filter(target => target.Criterion['@odata.type'] === '#Model.AgeCriterion'),
      campaignId: campaignModel.Id,
    }));

    viewModel = extend(viewModel, modelToViewModelForCompanyNameTargets({
      model: targetCriterionModel.filter(target => target.Criterion['@odata.type'] === '#Model.CompanyNameCriterion'),
      campaignId: campaignModel.Id,
    }));

    viewModel = extend(viewModel, modelToViewModelForIndustryTargets({
      model: targetCriterionModel.filter(target => target.Criterion['@odata.type'] === '#Model.IndustryCriterion'),
      campaignId: campaignModel.Id,
    }));

    viewModel = extend(viewModel, modelToViewModelForJobFunctionTargets({
      model: targetCriterionModel.filter(target => target.Criterion['@odata.type'] === '#Model.JobFunctionCriterion'),
      campaignId: campaignModel.Id,
    }));

    if (viewModel.BidStrategyName) {
      viewModel.Portofolio = viewModel.BiddingScheme;
    }
  }
  return viewModel;
};
